import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
  Stack,
  Alert,
  Snackbar,
  FormHelperText,
  FormControl,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Kbd,
  alpha,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Select,
  MenuItem
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  ProductionQuantityLimits as ProductIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Straighten as UnitIcon,
  Receipt as TaxIcon,
  QrCode as BarcodeIcon,
  Inventory as InventoryIcon,
  Inventory2 as QuantityIcon,
  Image as ImageIcon,
  Add as AddIcon,
  Edit as EditIcon,
  KeyboardReturn as EnterIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Help as HelpIcon,
  Inventory2 as RepackIcon,
  InfoOutlined as InfoIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import categoryService from '../../services/categoryService';
import unitService from '../../services/unitService';
import taxService from '../../services/taxService';
import { supplierService } from '../../services/supplierService';
import { getCurrentUser } from '../../services/authService';
import productService from '../../services/productService';
import purchaseOrderService from '../../services/purchaseOrderService';
import companySettingsService from '../../services/companySettingsService';
import { toast } from 'react-hot-toast';
import { useSnackbar } from 'notistack';

const logDebug = (message, data) => {
  console.log(`[ProductForm Debug] ${message}:`, data);
};

/** External HSN lookup (embedded in dialog). Source: https://hsn.codes/ */
const HSN_LOOKUP_URL = 'https://hsn.codes/';

const ProductFormEnhanced = ({
  initialData = null,
  onSubmit,
  onCancel,
  isViewMode = false,
  /** Tighter layout for POS / purchase embedded dialogs (less padding, shorter image area). */
  compact = false,
  /** Increment when wrapping dialog opens so first field can focus after the enter transition (Products list). */
  focusSession,
  // Accept pre-fetched data as props to avoid redundant API calls
  categoriesProp = null,
  unitsProp = null,
  taxesProp = null
}) => {
  const user = getCurrentUser();
  const companyId = user?.companyId;
  const { enqueueSnackbar } = useSnackbar();

  // Refs for keyboard navigation
  const firstInputRef = useRef(null);
  const submitButtonRef = useRef(null);
  const quickCategoryNameRef = useRef(null);
  const quickUnitNameRef = useRef(null);
  const quickTaxNameRef = useRef(null);

  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    printName: '',
    sku: '',
    productCode: '',
    categoryId: '',
    unitId: '',
    taxId: '',
    hsnCode: '',
    purchasePrice: '',
    mrp: '',
    sellingPrice: '', // Retailer rate
    withTax: '', // Retailer rate with tax (UI only)
    wholesalePrice: '', // Wholesale rate
    wholesaleWithTax: '', // Wholesale rate with tax (UI only)
    minSalePrice: '',
    quantity: '0',
    reorderLevel: '',
    imageFile: null,
    imagePreview: '',
    imageUrl: '',
    status: 'ACTIVE',
    manageable: true,
    // Sub Unit 1 fields
    subUnit1Id: '',
    subUnit1ConversionRatio: '',
    subUnit1SellingPrice: '',
    // Sub Unit 2 fields
    subUnit2Id: '',
    subUnit2ConversionRatio: '',
    subUnit2SellingPrice: '',
    supplierId: ''
  });

  // Track which field was last edited to prevent circular updates
  const [lastEditedPriceField, setLastEditedPriceField] = useState(null);

  // Entity lists - initialize with props if provided
  const [categories, setCategories] = useState(categoriesProp || []);
  const [units, setUnits] = useState(unitsProp || []);
  const [taxes, setTaxes] = useState(taxesProp || []);
  const [suppliers, setSuppliers] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingTaxes, setLoadingTaxes] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Error and validation
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [imageError, setImageError] = useState('');

  // Quick create dialogs
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [taxDialogOpen, setTaxDialogOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [hsnLookupDialogOpen, setHsnLookupDialogOpen] = useState(false);

  // Quick create form data
  const [newCategory, setNewCategory] = useState({ name: '' });
  const [newUnit, setNewUnit] = useState({ name: '', abbreviation: '' });
  const [newTax, setNewTax] = useState({ name: '', rate: '' });

  // Auto-complete selected values
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedTax, setSelectedTax] = useState(null);
  const [selectedSubUnit1, setSelectedSubUnit1] = useState(null);
  const [selectedSubUnit2, setSelectedSubUnit2] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [activeFormTab, setActiveFormTab] = useState(0);
  const [enableBatchWise, setEnableBatchWise] = useState(false);

  // Load company-level batch/expiry feature toggle
  useEffect(() => {
    let cancelled = false;
    const loadBatchWiseSetting = async () => {
      try {
        const data = await companySettingsService.getSettings();
        if (cancelled) return;
        setEnableBatchWise(Boolean(data?.enableBatchWise ?? false));
      } catch (e) {
        if (!cancelled) setEnableBatchWise(false);
      }
    };
    loadBatchWiseSetting();
    return () => {
      cancelled = true;
    };
  }, []);

  // If the active tab index is out of range for the current tab set, reset to tab 0.
  // Tabs: 0=Product Details, [1=Batch Details if enabled], 1or2=Repacking
  useEffect(() => {
    const maxValidTab = enableBatchWise ? 2 : 1;
    if (activeFormTab > maxValidTab) {
      setActiveFormTab(0);
    }
  }, [enableBatchWise, activeFormTab]);

  // Batch tab state
  const [batchRows, setBatchRows] = useState([]);
  const [editingBatchIndex, setEditingBatchIndex] = useState(null);
  const [batchForm, setBatchForm] = useState({
    batchNumber: '',
    mfd: '',
    expiryDate: '',
    mrp: '',
    sellingPrice: '',
    purchasePrice: '',
    quantity: '',
    status: 'ACTIVE'
  });

  // ─── Repacking tab state ─────────────────────────────────────────────────────
  const REPACK_COLUMNS = ['name', 'uom', 'unit', 'mrp', 'retail', 'wSales'];
  const createEmptyRepackRow = (id) => ({ id, name: '', unitId: null, unit: '', mrp: '', retail: '', wSales: '' });
  const repackNextIdRef = useRef(2);
  const [repackRows, setRepackRows] = useState([createEmptyRepackRow(1)]);
  const [repackOpenUomIdx, setRepackOpenUomIdx] = useState(null);
  const [repackSaving, setRepackSaving] = useState(false);
  const [repackLoaded, setRepackLoaded] = useState(false);
  const [repackError, setRepackError] = useState('');
  const repackNameRefs = useRef([]);
  const repackUomRefs = useRef([]);
  const repackUnitRefs = useRef([]);
  const repackMrpRefs = useRef([]);
  const repackRetailRefs = useRef([]);
  const repackWSalesRefs = useRef([]);
  const repackHandleTabRef = useRef(null);
  /** Skip advancing after UOM Select closes via Escape/backdrop (same pattern as POS keyboard). */
  const repackSkipUomTabAdvanceRef = useRef(false);

  const repackFocusField = useCallback((key, rowIndex) => {
    const refMap = {
      name: repackNameRefs, uom: repackUomRefs, unit: repackUnitRefs,
      mrp: repackMrpRefs, retail: repackRetailRefs, wSales: repackWSalesRefs,
    };
    const refs = refMap[key];
    if (refs?.current[rowIndex]) {
      refs.current[rowIndex].focus?.();
      if (key === 'uom') setRepackOpenUomIdx(rowIndex);
    }
  }, []);

  const repackAddRow = useCallback(() => {
    setRepackRows((prev) => {
      const newLen = prev.length;
      setTimeout(() => repackNameRefs.current[newLen]?.focus?.(), 80);
      return [...prev, createEmptyRepackRow(repackNextIdRef.current++)];
    });
  }, []);

  const repackHandleTabEnter = useCallback((rowIndex, currentKey) => {
    const idx = REPACK_COLUMNS.indexOf(currentKey);
    if (idx < 0) return;
    if (idx + 1 < REPACK_COLUMNS.length) {
      setTimeout(() => repackFocusField(REPACK_COLUMNS[idx + 1], rowIndex), 50);
    } else {
      repackAddRow();
    }
  }, [repackFocusField, repackAddRow]); // eslint-disable-line react-hooks/exhaustive-deps

  repackHandleTabRef.current = repackHandleTabEnter;

  const repackUpdateRow = useCallback((rowIndex, field, value) => {
    setRepackError('');
    setRepackRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, [field]: value } : r)));
  }, []);

  const repackDeleteRow = useCallback((rowIndex) => {
    setRepackRows((prev) => {
      if (prev.length <= 1) return [createEmptyRepackRow(repackNextIdRef.current++)];
      return prev.filter((_, i) => i !== rowIndex);
    });
  }, []);

  const loadRepackVariants = useCallback(async () => {
    if (!initialData?.id) return;
    setRepackLoaded(false);
    try {
      const variants = await productService.getRepackedVariants(initialData.id);
      if (variants?.length > 0) {
        const mapped = variants.map((v, i) => ({
          id: 1000 + i,
          name: v.name || '',
          unitId: v.unit?.id || null,
          unit: parseFloat(v.parentConversionRatio) || '',
          mrp: parseFloat(v.mrp) || '',
          retail: parseFloat(v.sellingPrice) || '',
          wSales: parseFloat(v.wholesalePrice) || '',
          existingId: v.id,
        }));
        setRepackRows([...mapped, createEmptyRepackRow(repackNextIdRef.current++)]);
      } else {
        setRepackRows([createEmptyRepackRow(repackNextIdRef.current++)]);
      }
    } catch (err) {
      console.error('Error loading repacked variants:', err);
      setRepackRows([createEmptyRepackRow(repackNextIdRef.current++)]);
    } finally {
      setRepackLoaded(true);
    }
  }, [initialData?.id]);

  const handleSaveRepacking = async () => {
    if (!initialData?.id) return;
    setRepackError('');

    // Validate: need at least a name and a UOM; unit qty defaults to 1 if blank
    const validRows = repackRows.filter((r) => (r.name?.trim() || '').length > 0 && r.unitId != null);
    if (validRows.length === 0) {
      setRepackError('Please fill in at least the Variant Name and UOM (Select Unit) for each row before saving.');
      return;
    }

    setRepackSaving(true);
    try {
      let created = 0, updated = 0;
      for (const row of validRows) {
        const convRatio = parseFloat(row.unit) > 0 ? parseFloat(row.unit) : 1;
        const payload = {
          name: row.name.trim(),
          unitId: row.unitId,
          parentConversionRatio: convRatio,
          sellingPrice: parseFloat(row.retail) || 0,
          wholesalePrice: parseFloat(row.wSales) || 0,
          minSalePrice: parseFloat(row.retail) || 0,
          mrp: parseFloat(row.mrp) || 0,
          purchasePrice: 0,
        };
        if (row.existingId) {
          await productService.updateRepackedVariant(row.existingId, payload);
          updated++;
        } else {
          await productService.createRepackedVariant(initialData.id, payload);
          created++;
        }
      }
      enqueueSnackbar(`Repacking variants saved (${created} new, ${updated} updated)`, { variant: 'success' });
      loadRepackVariants();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save repacking variants';
      setRepackError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setRepackSaving(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!isViewMode && !loading) {
          submitButtonRef.current?.click();
        }
      }
      // Escape to cancel
      if (e.key === 'Escape' && !categoryDialogOpen && !unitDialogOpen && !taxDialogOpen) {
        onCancel();
      }
      // Ctrl + H for help
      if ((e.ctrlKey || e.metaKey) && e.key === 'h') {
        e.preventDefault();
        setHelpDialogOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onCancel, isViewMode, loading, categoryDialogOpen, unitDialogOpen, taxDialogOpen]);

  // Focus first field after dialog transition (or legacy mount when `focusSession` is not used).
  useEffect(() => {
    if (isViewMode) return;
    const focusFirst = () => {
      const el = firstInputRef.current;
      if (el && typeof el.focus === 'function') {
        el.focus({ preventScroll: true });
      }
    };
    if (typeof focusSession === 'number' && focusSession > 0) {
      const delays = [0, 80, 200];
      const timers = delays.map((ms) => window.setTimeout(focusFirst, ms));
      return () => timers.forEach(clearTimeout);
    }
    const t = window.setTimeout(focusFirst, 100);
    return () => clearTimeout(t);
  }, [focusSession, isViewMode]);

  // Update state if props change
  useEffect(() => {
    if (categoriesProp && categoriesProp.length > 0) {
      console.log("ProductFormEnhanced: Using categories from props");
      setCategories(categoriesProp);
    }
  }, [categoriesProp]);

  useEffect(() => {
    if (unitsProp && unitsProp.length > 0) {
      console.log("ProductFormEnhanced: Using units from props");
      setUnits(unitsProp);
    }
  }, [unitsProp]);

  useEffect(() => {
    if (taxesProp && taxesProp.length > 0) {
      console.log("ProductFormEnhanced: Using taxes from props");
      setTaxes(taxesProp);
    }
  }, [taxesProp]);

  // Fetch data ONLY if not provided as props
  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      // Only fetch if not provided as props
      if (categoriesProp || categories.length > 0) {
        console.log("ProductFormEnhanced: Categories already available, skipping fetch");
        return;
      }

      try {
        setLoadingCategories(true);
        const response = await categoryService.getAllCategories();
        if (isMounted && response?.content) {
          console.log("ProductFormEnhanced: Fetched categories from API");
          setCategories(response.content);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      } finally {
        if (isMounted) setLoadingCategories(false);
      }
    };

    const fetchUnits = async () => {
      // Only fetch if not provided as props
      if (unitsProp || units.length > 0) {
        console.log("ProductFormEnhanced: Units already available, skipping fetch");
        return;
      }

      try {
        setLoadingUnits(true);
        const response = await unitService.getAllUnits();
        if (isMounted && response?.content) {
          console.log("ProductFormEnhanced: Fetched units from API");
          setUnits(response.content);
        }
      } catch (err) {
        console.error("Error fetching units:", err);
      } finally {
        if (isMounted) setLoadingUnits(false);
      }
    };

    const fetchTaxes = async () => {
      // Only fetch if not provided as props
      if (taxesProp || taxes.length > 0) {
        console.log("ProductFormEnhanced: Taxes already available, skipping fetch");
        return;
      }

      try {
        setLoadingTaxes(true);
        const data = await productService.getTaxes();
        if (isMounted && Array.isArray(data)) {
          console.log("ProductFormEnhanced: Fetched taxes from API");
          setTaxes(data);
        }
      } catch (err) {
        console.error('Error fetching taxes:', err);
      } finally {
        if (isMounted) setLoadingTaxes(false);
      }
    };

    const fetchSuppliers = async () => {
      try {
        setLoadingSuppliers(true);
        let list = [];
        if (companyId) {
          try {
            list = await supplierService.getSuppliersByCompany(companyId);
          } catch {
            list = [];
          }
        }
        if (list.length === 0) {
          const response = await supplierService.getAllSuppliers();
          list = response?.content ?? (Array.isArray(response) ? response : []);
        }
        if (isMounted && Array.isArray(list)) {
          setSuppliers(list);
        }
      } catch (err) {
        console.error('Error fetching suppliers:', err);
      } finally {
        if (isMounted) setLoadingSuppliers(false);
      }
    };

    fetchCategories();
    fetchUnits();
    fetchTaxes();
    fetchSuppliers();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Initialize form with existing data
  useEffect(() => {
    if (categories.length > 0 && units.length > 0) {
      if (initialData) {
        try {
          let categoryId = '';
          if (initialData.categoryId) categoryId = String(initialData.categoryId);
          else if (initialData.category?.id) categoryId = String(initialData.category.id);

          let unitId = '';
          if (initialData.unitId) unitId = String(initialData.unitId);
          else if (initialData.unit?.id) unitId = String(initialData.unit.id);

          let taxId = '';
          if (initialData.taxId) taxId = String(initialData.taxId);
          else if (initialData.tax?.id) taxId = String(initialData.tax.id);

          let subUnit1Id = '';
          if (initialData.subUnit1Id) subUnit1Id = String(initialData.subUnit1Id);
          else if (initialData.subUnit1?.id) subUnit1Id = String(initialData.subUnit1.id);

          let subUnit2Id = '';
          if (initialData.subUnit2Id) subUnit2Id = String(initialData.subUnit2Id);
          else if (initialData.subUnit2?.id) subUnit2Id = String(initialData.subUnit2.id);

          let supplierId = '';
          if (initialData.supplierId) supplierId = String(initialData.supplierId);
          else if (initialData.supplier?.id) supplierId = String(initialData.supplier.id);

          // Calculate withTax based on sellingPrice and tax rate (retailer)
          const sellingPrice = parseFloat(initialData.sellingPrice) || 0;
          let withTaxValue = '';
          if (taxId) {
            const tax = taxes.find(t => String(t.id) === taxId);
            if (tax && tax.rate) {
              withTaxValue = (sellingPrice * (1 + tax.rate / 100)).toFixed(2);
            } else {
              withTaxValue = sellingPrice.toString();
            }
          } else {
            withTaxValue = sellingPrice.toString();
          }

          // Wholesale: price and with-tax
          const wholesalePrice = parseFloat(initialData.wholesalePrice) || 0;
          let wholesaleWithTaxValue = '';
          if (taxId && wholesalePrice > 0) {
            const tax = taxes.find(t => String(t.id) === taxId);
            if (tax && tax.rate) {
              wholesaleWithTaxValue = (wholesalePrice * (1 + tax.rate / 100)).toFixed(2);
            } else {
              wholesaleWithTaxValue = wholesalePrice.toString();
            }
          } else if (wholesalePrice > 0) {
            wholesaleWithTaxValue = wholesalePrice.toString();
          }

          setFormData({
            name: initialData.name || '',
            printName: initialData.printName || '',
            sku: initialData.sku || '',
            productCode: initialData.productCode || '',
            categoryId,
            unitId,
            taxId,
            hsnCode: initialData.hsnCode || '',
            purchasePrice: initialData.purchasePrice || '',
            mrp: initialData.mrp || '',
            sellingPrice: initialData.sellingPrice || '',
            withTax: withTaxValue,
            wholesalePrice: initialData.wholesalePrice != null && initialData.wholesalePrice !== '' ? String(initialData.wholesalePrice) : '',
            wholesaleWithTax: wholesaleWithTaxValue,
            minSalePrice: initialData.minSalePrice || '',
            quantity: initialData.quantity || '0',
            reorderLevel: initialData.reorderLevel || '',
            imageFile: null,
            imagePreview: '',
            imageUrl: initialData.imageUrl || '',
            status: initialData.status || 'ACTIVE',
            manageable: typeof initialData.manageable === 'boolean' ? initialData.manageable : true,
            // Sub Unit 1
            subUnit1Id,
            subUnit1ConversionRatio: initialData.subUnit1ConversionRatio || '',
            subUnit1SellingPrice: initialData.subUnit1SellingPrice || '',
            // Sub Unit 2
            subUnit2Id,
            subUnit2ConversionRatio: initialData.subUnit2ConversionRatio || '',
            subUnit2SellingPrice: initialData.subUnit2SellingPrice || '',
            supplierId
          });

          // Set autocomplete selections
          if (categoryId) {
            const cat = categories.find(c => String(c.id) === categoryId);
            setSelectedCategory(cat || null);
          } else {
            setSelectedCategory(null);
          }

          if (unitId) {
            const unit = units.find(u => String(u.id) === unitId);
            setSelectedUnit(unit || null);
          } else {
            setSelectedUnit(null);
          }

          if (taxId) {
            const tax = taxes.find(t => String(t.id) === taxId);
            setSelectedTax(tax || null);
          }

          if (subUnit1Id) {
            const subUnit1 = units.find(u => String(u.id) === subUnit1Id);
            setSelectedSubUnit1(subUnit1 || null);
          }

          if (subUnit2Id) {
            const subUnit2 = units.find(u => String(u.id) === subUnit2Id);
            setSelectedSubUnit2(subUnit2 || null);
          }

          if (supplierId) {
            const supplier = suppliers.find(s => String(s.id) === supplierId);
            setSelectedSupplier(supplier || null);
          } else {
            setSelectedSupplier(null);
          }

          if (initialData.imageUrl) {
            setFormData(prev => ({
              ...prev,
              imagePreview: productService.getProductImageUrl(initialData.imageUrl)
            }));
          }

        } catch (err) {
          console.error('Error setting form data:', err);
        }
      } else {
        // Set default unit to PCS if available
        const pcsUnit = units.find(u => u.name?.toUpperCase() === 'PCS' || u.abbreviation?.toUpperCase() === 'PCS');
        if (pcsUnit) {
          setFormData(prev => ({
            ...prev,
            unitId: String(pcsUnit.id)
          }));
          setSelectedUnit(pcsUnit);
        }
      }
    }
  }, [initialData, categories, units, taxes, suppliers]);

  useEffect(() => {
    const loadProductBatches = async () => {
      if (!initialData?.id) {
        setBatchRows([]);
        return;
      }
      if (!enableBatchWise) {
        setBatchRows([]);
        return;
      }
      try {
        const rows = await purchaseOrderService.getProductBatchesByProduct(initialData.id, null, true);
        setBatchRows(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error('Error loading product batches in product form:', error);
        setBatchRows([]);
      }
    };
    loadProductBatches();
  }, [initialData?.id, enableBatchWise]);

  // Load repacking variants when the repacking tab becomes active
  const repackTabIndex = enableBatchWise ? 2 : 1;
  useEffect(() => {
    if (activeFormTab === repackTabIndex && initialData?.id && !repackLoaded) {
      loadRepackVariants();
    }
  }, [activeFormTab, repackTabIndex, initialData?.id, repackLoaded, loadRepackVariants]);

  useEffect(() => {
    // Keep batch prices in sync with product details unless user overrides.
    setBatchForm((prev) => ({
      ...prev,
      mrp: prev.mrp === '' ? (formData.mrp || '') : prev.mrp,
      sellingPrice: prev.sellingPrice === '' ? (formData.sellingPrice || '') : prev.sellingPrice,
      purchasePrice: prev.purchasePrice === '' ? (formData.purchasePrice || '') : prev.purchasePrice
    }));
  }, [formData.mrp, formData.sellingPrice, formData.purchasePrice]);

  const handleBatchInputChange = (e) => {
    const { name, value } = e.target;
    setBatchForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetBatchForm = () => {
    setBatchForm({
      batchNumber: '',
      mfd: '',
      expiryDate: '',
      mrp: formData.mrp || '',
      sellingPrice: formData.sellingPrice || '',
      purchasePrice: formData.purchasePrice || '',
      quantity: '',
      status: 'ACTIVE'
    });
    setEditingBatchIndex(null);
  };

  const handleAddOrUpdateBatchRow = () => {
    if (!batchForm.batchNumber?.trim()) {
      toast.error('Batch number is required');
      return;
    }
    const existingRow = editingBatchIndex !== null ? batchRows[editingBatchIndex] : null;
    const preservedServerId =
      existingRow && existingRow.id != null && existingRow.id !== ''
        ? existingRow.id
        : undefined;
    const nextRow = {
      ...(existingRow || {}),
      ...(preservedServerId !== undefined ? { id: preservedServerId } : {}),
      batchNumber: batchForm.batchNumber.trim(),
      mfd: batchForm.mfd || null,
      expiryDate: batchForm.expiryDate || null,
      mrp: batchForm.mrp !== '' ? Number(batchForm.mrp) : null,
      sellingPrice: batchForm.sellingPrice !== '' ? Number(batchForm.sellingPrice) : null,
      purchasePrice: batchForm.purchasePrice !== '' ? Number(batchForm.purchasePrice) : null,
      quantity: batchForm.quantity !== '' ? Number(batchForm.quantity) : 0,
      status: batchForm.status || 'ACTIVE'
    };

    setBatchRows((prev) => {
      if (editingBatchIndex !== null) {
        const updated = [...prev];
        updated[editingBatchIndex] = nextRow;
        return updated;
      }
      return [...prev, { ...nextRow, _isNew: true }];
    });
    resetBatchForm();
  };

  const handleEditBatchRow = (index) => {
    const row = batchRows[index];
    setBatchForm({
      batchNumber: row.batchNumber || '',
      mfd: row.mfd || row.manufacturingDate || '',
      expiryDate: row.expiryDate || '',
      mrp: row.mrp ?? '',
      sellingPrice: row.sellingPrice ?? '',
      purchasePrice: row.purchasePrice ?? '',
      quantity: row.quantity ?? '',
      status: row.status || 'ACTIVE'
    });
    setEditingBatchIndex(index);
    setActiveFormTab(1);
  };

  const handleDeleteBatchRow = (index) => {
    setBatchRows((prev) => prev.filter((_, i) => i !== index));
    if (editingBatchIndex === index) {
      resetBatchForm();
    }
  };

  // Auto-calculate between sellingPrice and withTax based on tax rate
  useEffect(() => {
    const taxRate = selectedTax?.rate || 0;

    // Skip if neither field has a value
    if (!formData.sellingPrice && !formData.withTax) {
      return;
    }

    // If no tax, both fields should be the same
    if (!taxRate || taxRate === 0) {
      // Only sync when the edited field has a value; do not repopulate when clearing
      if (lastEditedPriceField === 'sellingPrice' && formData.sellingPrice) {
        setFormData(prev => ({ ...prev, withTax: formData.sellingPrice }));
      } else if (lastEditedPriceField === 'withTax' && formData.withTax) {
        setFormData(prev => ({ ...prev, sellingPrice: formData.withTax }));
      }
      return;
    }

    // Calculate based on which field was last edited
    if (lastEditedPriceField === 'sellingPrice' && formData.sellingPrice) {
      const sellingPrice = parseFloat(formData.sellingPrice);
      if (!isNaN(sellingPrice) && sellingPrice >= 0) {
        const withTaxValue = sellingPrice * (1 + taxRate / 100);
        setFormData(prev => ({
          ...prev,
          withTax: withTaxValue.toFixed(2)
        }));
      }
    } else if (lastEditedPriceField === 'withTax' && formData.withTax) {
      const withTaxValue = parseFloat(formData.withTax);
      if (!isNaN(withTaxValue) && withTaxValue >= 0) {
        const sellingPriceValue = withTaxValue / (1 + taxRate / 100);
        setFormData(prev => ({
          ...prev,
          sellingPrice: sellingPriceValue.toFixed(2)
        }));
      }
    } else if (formData.sellingPrice && !lastEditedPriceField) {
      // When tax is first selected or changed, calculate withTax from sellingPrice
      const sellingPrice = parseFloat(formData.sellingPrice);
      if (!isNaN(sellingPrice) && sellingPrice >= 0) {
        const withTaxValue = sellingPrice * (1 + taxRate / 100);
        setFormData(prev => ({
          ...prev,
          withTax: withTaxValue.toFixed(2)
        }));
      }
    }
  }, [formData.sellingPrice, formData.withTax, selectedTax, lastEditedPriceField]);

  // Auto-calculate between wholesalePrice and wholesaleWithTax based on tax rate
  useEffect(() => {
    const taxRate = selectedTax?.rate || 0;
    if (!formData.wholesalePrice && !formData.wholesaleWithTax) return;
    if (!taxRate || taxRate === 0) {
      if (lastEditedPriceField === 'wholesalePrice' && formData.wholesalePrice) {
        setFormData(prev => ({ ...prev, wholesaleWithTax: formData.wholesalePrice }));
      } else if (lastEditedPriceField === 'wholesaleWithTax' && formData.wholesaleWithTax) {
        setFormData(prev => ({ ...prev, wholesalePrice: formData.wholesaleWithTax }));
      }
      return;
    }
    if (lastEditedPriceField === 'wholesalePrice' && formData.wholesalePrice) {
      const wholesalePrice = parseFloat(formData.wholesalePrice);
      if (!isNaN(wholesalePrice) && wholesalePrice >= 0) {
        setFormData(prev => ({ ...prev, wholesaleWithTax: (wholesalePrice * (1 + taxRate / 100)).toFixed(2) }));
      }
    } else if (lastEditedPriceField === 'wholesaleWithTax' && formData.wholesaleWithTax) {
      const wholesaleWithTaxValue = parseFloat(formData.wholesaleWithTax);
      if (!isNaN(wholesaleWithTaxValue) && wholesaleWithTaxValue >= 0) {
        setFormData(prev => ({ ...prev, wholesalePrice: (wholesaleWithTaxValue / (1 + taxRate / 100)).toFixed(2) }));
      }
    } else if (formData.wholesalePrice && !lastEditedPriceField) {
      const wholesalePrice = parseFloat(formData.wholesalePrice);
      if (!isNaN(wholesalePrice) && wholesalePrice >= 0) {
        setFormData(prev => ({ ...prev, wholesaleWithTax: (wholesalePrice * (1 + taxRate / 100)).toFixed(2) }));
      }
    }
  }, [formData.wholesalePrice, formData.wholesaleWithTax, selectedTax, lastEditedPriceField]);

  // Auto-calculate sub-unit prices when main price or conversion ratios change
  useEffect(() => {
    if (formData.sellingPrice && formData.subUnit1ConversionRatio) {
      const mainPrice = parseFloat(formData.sellingPrice);
      const ratio1 = parseFloat(formData.subUnit1ConversionRatio);

      if (mainPrice > 0 && ratio1 > 0) {
        const sub1Price = mainPrice / ratio1;
        setFormData(prev => ({
          ...prev,
          subUnit1SellingPrice: sub1Price.toFixed(2)
        }));

        // If Sub Unit 2 ratio exists, calculate Sub Unit 2 price
        if (formData.subUnit2ConversionRatio) {
          const ratio2 = parseFloat(formData.subUnit2ConversionRatio);
          if (ratio2 > 0) {
            const sub2Price = sub1Price / ratio2;
            setFormData(prev => ({
              ...prev,
              subUnit2SellingPrice: sub2Price.toFixed(2)
            }));
          }
        }
      }
    } else {
      // Clear prices if main price or ratio is missing
      if (!formData.sellingPrice || !formData.subUnit1ConversionRatio) {
        setFormData(prev => ({ ...prev, subUnit1SellingPrice: '' }));
      }
      if (!formData.subUnit1ConversionRatio || !formData.subUnit2ConversionRatio) {
        setFormData(prev => ({ ...prev, subUnit2SellingPrice: '' }));
      }
    }
  }, [formData.sellingPrice, formData.subUnit1ConversionRatio, formData.subUnit2ConversionRatio]);

  const openHsnLookupDialog = () => {
    setHsnLookupDialogOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for text fields (max 25 characters)
    if (['name', 'sku', 'hsnCode', 'productCode'].includes(name)) {
      if (value.length > 50) return;
    }

    // Validation for number fields (max 6 digits)
    if (['purchasePrice', 'sellingPrice', 'wholesalePrice', 'mrp', 'quantity', 'reorderLevel', 'subUnit1SellingPrice', 'subUnit2SellingPrice'].includes(name)) {
      if (value.length > 6) return;
    }

    // Track which price field was edited for tax calculation
    if (['sellingPrice', 'withTax', 'wholesalePrice', 'wholesaleWithTax'].includes(name)) {
      setLastEditedPriceField(name);
    }

    // Allow empty value for number fields to enable clearing/backspace
    if (value === '') {
      if (name === 'sellingPrice') {
        setFormData(prev => ({ ...prev, sellingPrice: '', withTax: '' }));
      } else if (name === 'withTax') {
        setFormData(prev => ({ ...prev, withTax: '', sellingPrice: '' }));
      } else if (name === 'wholesalePrice') {
        setFormData(prev => ({ ...prev, wholesalePrice: '', wholesaleWithTax: '' }));
      } else if (name === 'wholesaleWithTax') {
        setFormData(prev => ({ ...prev, wholesaleWithTax: '', wholesalePrice: '' }));
      } else {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
      if (formErrors[name]) {
        setFormErrors({ ...formErrors, [name]: null });
      }
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageError('');

    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setImageError('Unsupported format. Please upload JPEG or PNG.');
      toast.error('Please upload a valid image file (JPEG or PNG)');
      return;
    }

    const minSize = 1 * 1024; // 1KB
    const maxSize = 500 * 1024; // 500KB

    if (file.size < minSize || file.size > maxSize) {
      if (file.size < minSize) {
        setImageError('Image is smaller than 1KB. Please upload 1KB–500KB.');
      } else {
        setImageError('Image exceeds 500KB. Please upload 1KB–500KB.');
      }
      toast.error('Image size should be between 1KB and 500KB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: reader.result
      }));
    };
    reader.readAsDataURL(file);

    try {
      setUploadLoading(true);
      const imageUrl = await productService.uploadProductImage(file);
      setFormData(prev => ({ ...prev, imageUrl }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      imageFile: null,
      imagePreview: '',
      imageUrl: ''
    }));
    setImageError('');
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) errors.name = 'Product name is required';
    if (!formData.categoryId) errors.categoryId = 'Category is required';
    if (!formData.unitId) errors.unitId = 'Unit is required';

    // MRP is optional, but if provided must be valid
    if (formData.mrp && parseFloat(formData.mrp) < 0) {
      errors.mrp = 'MRP cannot be negative';
    }

    // Purchase price is optional, but if provided must be valid
    if (formData.purchasePrice && parseFloat(formData.purchasePrice) < 0) {
      errors.purchasePrice = 'Purchase price cannot be negative';
    }

    // Retailer rate (selling price) is required
    if (!formData.sellingPrice || parseFloat(formData.sellingPrice) <= 0) {
      errors.sellingPrice = 'Valid retailer rate is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);

      // SKU: send as-is. If empty, backend will auto-generate from company settings (prefix + pattern)
      const sku = formData.sku?.trim() || '';

      // Remove UI-only fields (withTax, wholesaleWithTax) from submitData
      const { withTax, wholesaleWithTax, ...dataToSubmit } = formData;

      const submitData = {
        ...dataToSubmit,
        printName: formData.printName?.trim() || null,
        sku,
        companyId: parseInt(companyId) || 1,
        categoryId: parseInt(formData.categoryId),
        unitId: parseInt(formData.unitId),
        taxId: formData.taxId ? parseInt(formData.taxId) : null,
        supplierId: formData.supplierId ? parseInt(formData.supplierId) : null,
        hsnCode: formData.hsnCode?.trim() || null,
        purchasePrice: parseFloat(formData.purchasePrice) || 0,
        mrp: formData.mrp ? parseFloat(formData.mrp) : null,
        sellingPrice: parseFloat(formData.sellingPrice) || 0,
        wholesalePrice: formData.wholesalePrice ? parseFloat(formData.wholesalePrice) : null,
        minSalePrice: parseFloat(formData.minSalePrice) || 0,
        quantity: parseInt(formData.quantity || '0'),
        reorderLevel: formData.reorderLevel ? parseInt(formData.reorderLevel) : null,
        status: formData.status || 'ACTIVE',
        imageUrl: formData.imageUrl,
        manageable: !!formData.manageable,
        // Sub Unit 1
        subUnit1Id: formData.subUnit1Id ? parseInt(formData.subUnit1Id) : null,
        subUnit1ConversionRatio: formData.subUnit1ConversionRatio ? parseFloat(formData.subUnit1ConversionRatio) : null,
        subUnit1SellingPrice: formData.subUnit1SellingPrice ? parseFloat(formData.subUnit1SellingPrice) : null,
        // Sub Unit 2
        subUnit2Id: formData.subUnit2Id ? parseInt(formData.subUnit2Id) : null,
        subUnit2ConversionRatio: formData.subUnit2ConversionRatio ? parseFloat(formData.subUnit2ConversionRatio) : null,
        subUnit2SellingPrice: formData.subUnit2SellingPrice ? parseFloat(formData.subUnit2SellingPrice) : null
      };

      delete submitData.imageFile;
      delete submitData.imagePreview;

      const batchPayload = enableBatchWise
        ? batchRows
          .filter((row) => row.batchNumber && String(row.batchNumber).trim())
          .map((row) => {
            const rawId = row.id;
            const hasServerId =
              rawId != null &&
              rawId !== '' &&
              Number.isInteger(Number(rawId)) &&
              Number(rawId) > 0;
            return {
              id: hasServerId ? Number(rawId) : null,
              batchNumber: String(row.batchNumber).trim(),
              mfd: row.mfd || row.manufacturingDate || null,
              expiryDate: row.expiryDate || null,
              mrp: row.mrp != null ? Number(row.mrp) : null,
              sellingPrice: row.sellingPrice != null ? Number(row.sellingPrice) : null,
              purchasePrice: row.purchasePrice != null ? Number(row.purchasePrice) : null,
              quantity: row.quantity != null ? Number(row.quantity) : 0,
              status: row.status || 'ACTIVE'
            };
          })
        : [];

      await onSubmit({
        productData: submitData,
        productBatches: batchPayload
      });
      toast.success(initialData ? 'Product updated successfully' : 'Product created successfully');
    } catch (err) {
      console.error('Error submitting form:', err);
      toast.error(err.response?.data?.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  // Quick create handlers
  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      const created = await categoryService.createCategory({
        name: newCategory.name.trim(),
        companyId: parseInt(companyId)
      });

      setCategories(prev => [...prev, created]);
      setSelectedCategory(created);
      setFormData(prev => ({ ...prev, categoryId: String(created.id) }));
      setCategoryDialogOpen(false);
      setNewCategory({ name: '' });
      toast.success('Category created successfully');
    } catch (err) {
      toast.error('Failed to create category');
    }
  };

  const handleCreateUnit = async () => {
    if (!newUnit.name.trim() || !newUnit.abbreviation.trim()) {
      toast.error('Unit name and abbreviation are required');
      return;
    }

    try {
      const created = await unitService.createUnit({
        name: newUnit.name.trim(),
        abbreviation: newUnit.abbreviation.trim(),
        companyId: parseInt(companyId)
      });

      setUnits(prev => [...prev, created]);
      setSelectedUnit(created);
      setFormData(prev => ({ ...prev, unitId: String(created.id) }));
      setUnitDialogOpen(false);
      setNewUnit({ name: '', abbreviation: '' });
      toast.success('Unit created successfully');
    } catch (err) {
      toast.error('Failed to create unit');
    }
  };

  const handleCreateTax = async () => {
    if (!newTax.name.trim() || !newTax.rate) {
      toast.error('Tax name and rate are required');
      return;
    }

    try {
      const created = await taxService.createTax({
        name: newTax.name.trim(),
        rate: parseFloat(newTax.rate),
        companyId: parseInt(companyId)
      });

      setTaxes(prev => [...prev, created]);
      setSelectedTax(created);
      setFormData(prev => ({ ...prev, taxId: String(created.id) }));
      setTaxDialogOpen(false);
      setNewTax({ name: '', rate: '' });
      toast.success('Tax created successfully');
    } catch (err) {
      toast.error('Failed to create tax');
    }
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return '-';
    
    // Handle array format [YYYY, M, D] from Jackson serialization
    if (Array.isArray(dateVal) && dateVal.length >= 3) {
      const year = dateVal[0];
      const month = String(dateVal[1]).padStart(2, '0');
      const day = String(dateVal[2]).padStart(2, '0');
      return `${day}/${month}/${year}`;
    }

    // Handle string format YYYY-MM-DD from input elements
    if (typeof dateVal === 'string' && dateVal.includes('-')) {
      const parts = dateVal.split('-');
      if (parts.length === 3) {
        return `${parts[2].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[0]}`;
      }
    }

    return dateVal;
  };

  const layout = compact
    ? {
        gridSpacing: 1.25,
        sectionPad: 1.25,
        sidePad: 1,
        tabMinH: 32,
        imgMaxH: 130,
        footerMt: 1,
        footerP: 1,
        submitSize: 'medium',
      }
    : {
        gridSpacing: 3,
        sectionPad: 3,
        sidePad: 2,
        tabMinH: 38,
        imgMaxH: 220,
        footerMt: 3,
        footerP: 2,
        submitSize: 'large',
      };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Paper elevation={0} sx={{ px: compact ? 1 : 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={activeFormTab}
          onChange={(e, val) => setActiveFormTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: layout.tabMinH,
            '& .MuiTab-root': {
              minHeight: layout.tabMinH,
              py: compact ? 0.25 : 0.5,
              fontSize: compact ? '0.7rem' : '0.75rem',
            },
          }}
        >
          <Tab label="Product Details" />
          {enableBatchWise && <Tab label="Batch Details" />}
          <Tab label="Repacking" icon={<RepackIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {activeFormTab === 0 && (
        <Grid container spacing={layout.gridSpacing}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            {/* All Fields in One Box */}
            <Paper elevation={0} sx={{ p: layout.sectionPad, border: '1px solid', borderColor: 'divider' }}>
              <Grid container columnSpacing={2} rowSpacing={compact ? 0.75 : 1}>
                {/* Row 1: Product Name and SKU */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    inputRef={firstInputRef}
                    fullWidth
                    required
                    size="small"
                    label="Product Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    autoComplete="off"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ProductIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="SKU (Auto-generated if empty)"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    autoComplete="off"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BarcodeIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Row 2: Category and Tax */}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    value={selectedCategory}
                    onChange={(event, newValue) => {
                      if (newValue === 'add-new') {
                        setCategoryDialogOpen(true);
                      } else {
                        setSelectedCategory(newValue);
                        setFormData(prev => ({
                          ...prev,
                          categoryId: newValue ? String(newValue.id) : ''
                        }));
                      }
                    }}
                    options={[...categories, 'add-new']}
                    getOptionLabel={(option) => {
                      if (option === 'add-new') return '+ Add New Category';
                      return option?.name || '';
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (option === 'add-new') return false;
                      return option?.id === value?.id;
                    }}
                    loading={loadingCategories}
                    disabled={isViewMode || loading}
                    autoHighlight
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option === 'add-new' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                            <AddIcon sx={{ mr: 1 }} fontSize="small" />
                            Add New Category
                          </Box>
                        ) : (
                          option.name
                        )}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category"
                        required
                        size="small"
                        error={!!formErrors.categoryId}
                        helperText={formErrors.categoryId}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <CategoryIcon color="primary" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    value={selectedTax}
                    onChange={(event, newValue) => {
                      if (newValue === 'add-new') {
                        setTaxDialogOpen(true);
                      } else {
                        setSelectedTax(newValue);
                        setFormData(prev => ({
                          ...prev,
                          taxId: newValue ? String(newValue.id) : ''
                        }));
                      }
                    }}
                    options={[...taxes.filter(t => (t.status === 'ACTIVE' || !t.status) || (selectedTax && t.id === selectedTax.id)), 'add-new']}
                    getOptionLabel={(option) => {
                      if (option === 'add-new') return '+ Add New Tax';
                      return option?.name || '';
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (option === 'add-new') return false;
                      return option?.id === value?.id;
                    }}
                    loading={loadingTaxes}
                    disabled={isViewMode || loading}
                    autoHighlight
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option === 'add-new' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                            <AddIcon sx={{ mr: 1 }} fontSize="small" />
                            Add New Tax
                          </Box>
                        ) : (
                          option.name
                        )}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tax (Optional)"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <TaxIcon color="primary" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Row 3: HSN Code */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="HSN Code"
                    name="hsnCode"
                    value={formData.hsnCode}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    error={!!formErrors.hsnCode}
                    helperText={formErrors.hsnCode}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BarcodeIcon color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Look up HSN on hsn.codes (opens in a window on top of this form)">
                            <span>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={openHsnLookupDialog}
                                disabled={isViewMode || loading}
                                aria-label="Open HSN lookup"
                              >
                                <SearchIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Quantity"
                    name="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <QuantityIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    helperText=""
                  />
                </Grid>

                {/* Row 4: MRP and Purchase Price */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="MRP"
                    name="mrp"
                    type="number"
                    value={formData.mrp}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    error={!!formErrors.mrp}
                    helperText={formErrors.mrp}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Purchase Price"
                    name="purchasePrice"
                    type="number"
                    value={formData.purchasePrice}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    error={!!formErrors.purchasePrice}
                    helperText={formErrors.purchasePrice}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>

                {/* Row 5: Retailer Rate and With Tax */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label="Retailer Rate"
                    name="sellingPrice"
                    type="number"
                    value={formData.sellingPrice}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    error={!!formErrors.sellingPrice}
                    helperText={formErrors.sellingPrice}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={selectedTax?.rate ? `Retailer With Tax (${selectedTax.rate}%)` : 'Retailer With Tax'}
                    name="withTax"
                    type="number"
                    value={formData.withTax}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      endAdornment: selectedTax?.rate ? (
                        <InputAdornment position="end">
                          <Chip
                            label="With GST"
                            size="small"
                            color="success"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </InputAdornment>
                      ) : undefined
                    }}
                    helperText={selectedTax?.rate ? `Inclusive of ${selectedTax.rate}% tax` : ''}
                  />
                </Grid>

                {/* Row 5b: Wholesale Rate and Wholesale With Tax */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Wholesale Rate"
                    name="wholesalePrice"
                    type="number"
                    value={formData.wholesalePrice}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={selectedTax?.rate ? `Wholesale With Tax (${selectedTax.rate}%)` : 'Wholesale With Tax'}
                    name="wholesaleWithTax"
                    type="number"
                    value={formData.wholesaleWithTax}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    helperText={selectedTax?.rate ? `Inclusive of ${selectedTax.rate}% tax` : ''}
                  />
                </Grid>

                {/* Row 6: Reorder Level and Status */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reorder Level"
                    name="reorderLevel"
                    type="number"
                    value={formData.reorderLevel}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <InventoryIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    value={formData.status}
                    onChange={(event, newValue) => {
                      setFormData(prev => ({ ...prev, status: newValue || 'ACTIVE' }));
                    }}
                    options={['ACTIVE', 'INACTIVE']}
                    disabled={isViewMode || loading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status"
                        size="small"
                      />
                    )}
                  />
                </Grid>

                {/* Main Unit — small gap after Reorder/Status so layout aligns with right column */}
                <Grid item xs={12} md={3} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }} fontWeight="bold" color="primary">
                    Main Unit
                  </Typography>
                  <Autocomplete
                    value={selectedUnit}
                    onChange={(event, newValue) => {
                      if (newValue === 'add-new') {
                        setUnitDialogOpen(true);
                      } else {
                        setSelectedUnit(newValue);
                        setFormData(prev => ({
                          ...prev,
                          unitId: newValue ? String(newValue.id) : ''
                        }));
                      }
                    }}
                    options={[...units, 'add-new']}
                    getOptionLabel={(option) => {
                      if (option === 'add-new') return '+ Add New Unit';
                      return option?.abbreviation || option?.name || '';
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (option === 'add-new') return false;
                      return option?.id === value?.id;
                    }}
                    loading={loadingUnits}
                    disabled={isViewMode || loading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Main Unit"
                        required
                        size="small"
                        error={!!formErrors.unitId}
                        helperText={formErrors.unitId}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <UnitIcon color="primary" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <TextField
                    fullWidth
                    required
                    label="Price"
                    name="sellingPrice"
                    type="number"
                    size="small"
                    value={formData.sellingPrice}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    error={!!formErrors.sellingPrice}
                    helperText={formErrors.sellingPrice}
                    sx={{ mt: 1 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>

                {/* Sub Unit 1 */}
                <Grid item xs={12} md={4.5} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }} fontWeight="bold">
                    Sub Unit 1
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center' }}>=</Typography>
                    <TextField
                      size="small"
                      name="subUnit1ConversionRatio"
                      type="number"
                      value={formData.subUnit1ConversionRatio}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty or valid number with max 2 decimal places
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      onWheel={(e) => {
                        e.target.blur(); // Prevent scrolling from changing value
                      }}
                      disabled={isViewMode || loading}
                      placeholder="Qty"
                      sx={{
                        width: 100,
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                    <Autocomplete
                      value={selectedSubUnit1}
                      onChange={(event, newValue) => {
                        if (newValue === 'add-new') {
                          setUnitDialogOpen(true);
                        } else {
                          setSelectedSubUnit1(newValue);
                          setFormData(prev => ({
                            ...prev,
                            subUnit1Id: newValue ? String(newValue.id) : '',
                            subUnit1ConversionRatio: newValue ? prev.subUnit1ConversionRatio : '',
                            subUnit1SellingPrice: newValue ? prev.subUnit1SellingPrice : ''
                          }));
                        }
                      }}
                      options={[...units, 'add-new']}
                      getOptionLabel={(option) => {
                        if (option === 'add-new') return '+ Add New Unit';
                        return option?.abbreviation || option?.name || '';
                      }}
                      isOptionEqualToValue={(option, value) => {
                        if (option === 'add-new') return false;
                        return option?.id === value?.id;
                      }}
                      loading={loadingUnits}
                      disabled={isViewMode || loading}
                      sx={{ flex: 1 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Sub Unit 1"
                          size="small"
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center', visibility: 'hidden' }}>=</Typography>
                    <TextField
                      size="small"
                      label="Price"
                      name="subUnit1SellingPrice"
                      type="number"
                      value={formData.subUnit1SellingPrice}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      disabled={isViewMode || loading}
                      placeholder="Auto or manual"
                      sx={{
                        width: 100,
                        flex: 1,
                        '& input[type=number]': { MozAppearance: 'textfield' },
                        '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                        '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                {/* Sub Unit 2 */}
                <Grid item xs={12} md={4.5} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }} fontWeight="bold">
                    Sub Unit 2
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center' }}>=</Typography>
                    <TextField
                      size="small"
                      name="subUnit2ConversionRatio"
                      type="number"
                      value={formData.subUnit2ConversionRatio}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty or valid number with max 2 decimal places
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      onWheel={(e) => {
                        e.target.blur(); // Prevent scrolling from changing value
                      }}
                      disabled={isViewMode || loading || !formData.subUnit1Id}
                      placeholder="Qty"
                      sx={{
                        width: 100,
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                    <Autocomplete
                      value={selectedSubUnit2}
                      onChange={(event, newValue) => {
                        if (newValue === 'add-new') {
                          setUnitDialogOpen(true);
                        } else {
                          setSelectedSubUnit2(newValue);
                          setFormData(prev => ({
                            ...prev,
                            subUnit2Id: newValue ? String(newValue.id) : '',
                            subUnit2ConversionRatio: newValue ? prev.subUnit2ConversionRatio : '',
                            subUnit2SellingPrice: newValue ? prev.subUnit2SellingPrice : ''
                          }));
                        }
                      }}
                      options={[...units, 'add-new']}
                      getOptionLabel={(option) => {
                        if (option === 'add-new') return '+ Add New Unit';
                        return option?.abbreviation || option?.name || '';
                      }}
                      isOptionEqualToValue={(option, value) => {
                        if (option === 'add-new') return false;
                        return option?.id === value?.id;
                      }}
                      loading={loadingUnits}
                      disabled={isViewMode || loading || !formData.subUnit1Id}
                      sx={{ flex: 1 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Sub Unit 2"
                          size="small"
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center', visibility: 'hidden' }}>=</Typography>
                    <TextField
                      size="small"
                      label="Price"
                      name="subUnit2SellingPrice"
                      type="number"
                      value={formData.subUnit2SellingPrice}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      disabled={isViewMode || loading || !formData.subUnit1Id}
                      placeholder="Auto or manual"
                      sx={{
                        width: 100,
                        flex: 1,
                        '& input[type=number]': { MozAppearance: 'textfield' },
                        '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                        '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Right Column - Image */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: layout.sidePad, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {formData.imagePreview ? (
                  <Box
                    component="img"
                    src={formData.imagePreview}
                    alt="Product"
                    sx={{
                      width: '100%',
                      maxHeight: layout.imgMaxH,
                      objectFit: 'contain',
                      mb: compact ? 1 : 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'grey.50'
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: layout.imgMaxH,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'grey.100',
                      border: '2px dashed',
                      borderColor: 'grey.300',
                      borderRadius: 2,
                      mb: 1.5,
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 62, color: 'grey.400' }} />
                  </Box>
                )}

                {!isViewMode && (
                  <Stack spacing={1} sx={{ width: '100%' }}>
                    <Button
                      variant="contained"
                      component="label"
                      disabled={loading || uploadLoading}
                      startIcon={uploadLoading ? <CircularProgress size={20} /> : <UploadIcon />}
                      fullWidth
                    >
                      {uploadLoading ? 'Uploading...' : 'Upload Image'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </Button>
                    {formData.imagePreview && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleRemoveImage}
                        disabled={loading || uploadLoading}
                        startIcon={<DeleteIcon />}
                        fullWidth
                      >
                        Remove Image
                      </Button>
                    )}
                  </Stack>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  Max size: 1KB - 500KB • Formats: JPEG, PNG
                </Typography>
                {imageError && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, textAlign: 'center' }}>
                    {imageError}
                  </Typography>
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.manageable}
                      onChange={(e) => setFormData(prev => ({ ...prev, manageable: e.target.checked }))}
                      disabled={isViewMode || loading}
                      color="primary"
                    />
                  }
                  label="Stock required"
                  sx={{ mt: 1 }}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Remark"
                  name="productCode"
                  value={formData.productCode}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  sx={{ mt: 1 }}
                  placeholder="Product Code / Remark"
                />

                <Autocomplete
                  fullWidth
                  value={selectedSupplier}
                  onChange={(event, newValue) => {
                    setSelectedSupplier(newValue);
                    setFormData(prev => ({
                      ...prev,
                      supplierId: newValue ? String(newValue.id) : ''
                    }));
                  }}
                  options={suppliers}
                  getOptionLabel={(option) => (option?.supplierCode ? `${option.name || ''} (${option.supplierCode})` : (option?.name || ''))}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  loading={loadingSuppliers}
                  disabled={isViewMode || loading}
                  autoHighlight
                  sx={{ mt: 1 }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.supplierCode ? `${option.name} - ${option.supplierCode}` : option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Supplier (Optional)"
                      size="small"
                    />
                  )}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Print Name (Optional)"
                  name="printName"
                  value={formData.printName}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  sx={{ mt: 1 }}
                  placeholder="Short name for bill printing"
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {enableBatchWise && activeFormTab === 1 && (
        <Paper elevation={0} sx={{ p: layout.sectionPad, mt: compact ? 1 : 2, border: '1px solid', borderColor: 'divider' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'repeat(8, minmax(120px, 1fr))'
              },
              gap: 1.5
            }}
          >
            <TextField fullWidth size="small" label="Batch No" name="batchNumber" value={batchForm.batchNumber} onChange={handleBatchInputChange} disabled={isViewMode || loading} />
            <TextField fullWidth size="small" label="MFD" type="date" name="mfd" value={batchForm.mfd} onChange={handleBatchInputChange} disabled={isViewMode || loading} InputLabelProps={{ shrink: true }} />
            <TextField fullWidth size="small" label="Expiry" type="date" name="expiryDate" value={batchForm.expiryDate} onChange={handleBatchInputChange} disabled={isViewMode || loading} InputLabelProps={{ shrink: true }} />
            <TextField fullWidth size="small" label="MRP" type="number" name="mrp" value={batchForm.mrp} onChange={handleBatchInputChange} disabled={isViewMode || loading} />
            <TextField fullWidth size="small" label="Selling Price" type="number" name="sellingPrice" value={batchForm.sellingPrice} onChange={handleBatchInputChange} disabled={isViewMode || loading} />
            <TextField fullWidth size="small" label="Purchase Price" type="number" name="purchasePrice" value={batchForm.purchasePrice} onChange={handleBatchInputChange} disabled={isViewMode || loading} />
            <TextField fullWidth size="small" label="Quantity" type="number" name="quantity" value={batchForm.quantity} onChange={handleBatchInputChange} disabled={isViewMode || loading} />
            <Autocomplete
              value={batchForm.status}
              onChange={(event, newValue) => setBatchForm((prev) => ({ ...prev, status: newValue || 'ACTIVE' }))}
              options={['ACTIVE', 'INACTIVE']}
              disabled={isViewMode || loading}
              renderInput={(params) => (
                <TextField {...params} label="Status" size="small" />
              )}
            />
          </Box>

          {!isViewMode && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddOrUpdateBatchRow}>
                {editingBatchIndex !== null ? 'Update Batch' : 'Add Batch'}
              </Button>
              <Button variant="outlined" onClick={resetBatchForm}>Clear</Button>
            </Box>
          )}

          <TableContainer sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Batch No</TableCell>
                  <TableCell>MFD</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>MRP</TableCell>
                  <TableCell>Selling</TableCell>
                  <TableCell>Purchase</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Status</TableCell>
                  {!isViewMode && <TableCell align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {batchRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isViewMode ? 8 : 9} align="center">No batch rows added</TableCell>
                  </TableRow>
                ) : batchRows.map((row, index) => (
                  <TableRow key={row.id || `${row.batchNumber}-${index}`}>
                    <TableCell>{row.batchNumber}</TableCell>
                    <TableCell>{formatDate(row.mfd || row.manufacturingDate)}</TableCell>
                    <TableCell>{formatDate(row.expiryDate)}</TableCell>
                    <TableCell>{row.mrp ?? '-'}</TableCell>
                    <TableCell>{row.sellingPrice ?? '-'}</TableCell>
                    <TableCell>{row.purchasePrice ?? '-'}</TableCell>
                    <TableCell>{row.quantity ?? 0}</TableCell>
                    <TableCell>{row.status || 'ACTIVE'}</TableCell>
                    {!isViewMode && (
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => handleEditBatchRow(index)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteBatchRow(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Repacking Tab Panel ──────────────────────────────────────────────── */}
      {activeFormTab === repackTabIndex && (
        <Paper elevation={0} sx={{ p: layout.sectionPad, mt: compact ? 1 : 2, border: '1px solid', borderColor: 'divider' }}>
          {!initialData?.id ? (
            /* Product not saved yet — variants need a parent product ID */
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: layout.sectionPad, bgcolor: 'grey.50', borderRadius: 2 }}>
              <InfoIcon color="info" />
              <Typography variant="body2" color="text.secondary">
                Save the product first, then come back to this tab to define repacked variants.
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
              '& input[type=number]': { MozAppearance: 'textfield' },
            }}>
              {/* Inline error */}
              {repackError && (
                <Alert severity="error" onClose={() => setRepackError('')} sx={{ mb: 2 }}>
                  {repackError}
                </Alert>
              )}

              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <RepackIcon sx={{ color: '#1a237e', fontSize: 22 }} />
                <Typography variant="subtitle1" fontWeight="800" color="#1a237e">
                  Repacked Variants
                </Typography>
                {formData.unit?.abbreviation || selectedUnit?.abbreviation ? (
                  <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    Main unit: <b>{selectedUnit?.abbreviation || selectedUnit?.name || '—'}</b>
                    &nbsp;•&nbsp;Current qty: <b>{formData.quantity || 0}</b>
                  </Typography>
                ) : null}
              </Box>

              {/* Variants grid */}
              <Box sx={{
                bgcolor: '#fff', borderRadius: '10px',
                overflow: 'hidden', border: '1px solid rgba(0,0,0,0.07)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f7f7fb' }}>
                      <TableCell sx={{ color: '#666', fontWeight: 700, fontSize: '0.7rem', width: 36 }}>#</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }}>Variant Name</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }}>UOM</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }} align="right">Unit (Qty)</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }} align="right">MRP</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }} align="right">Retail Rate</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: 800, fontSize: '0.75rem' }} align="right">Wholesale Rate</TableCell>
                      <TableCell width={36} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {repackRows.map((row, index) => (
                      <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#f9f9ff' } }}>
                        <TableCell sx={{ color: '#ccc', fontWeight: 700, py: 1 }}>{index + 1}</TableCell>

                        {/* Variant Name */}
                        <TableCell sx={{ py: 1 }}>
                          <TextField
                            fullWidth variant="standard" placeholder="Variant Name"
                            value={row.name}
                            onChange={(e) => repackUpdateRow(index, 'name', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), repackHandleTabRef.current?.(index, 'name'))}
                            inputRef={(el) => (repackNameRefs.current[index] = el)}
                            InputProps={{ disableUnderline: true, sx: { fontWeight: 600, fontSize: '0.85rem' } }}
                            disabled={isViewMode}
                          />
                        </TableCell>

                        {/* UOM */}
                        <TableCell sx={{ py: 1 }}>
                          <FormControl variant="standard" fullWidth>
                            <Select
                              open={repackOpenUomIdx === index}
                              onOpen={() => {
                                repackSkipUomTabAdvanceRef.current = false;
                                setRepackOpenUomIdx(index);
                              }}
                              onClose={() => {
                                setRepackOpenUomIdx(null);
                                const skip = repackSkipUomTabAdvanceRef.current;
                                repackSkipUomTabAdvanceRef.current = false;
                                if (!skip) {
                                  setTimeout(() => repackHandleTabRef.current?.(index, 'uom'), 50);
                                }
                              }}
                              inputRef={(el) => (repackUomRefs.current[index] = el)}
                              value={row.unitId ?? ''}
                              onChange={(e) => repackUpdateRow(index, 'unitId', e.target.value || null)}
                              MenuProps={{
                                onClose: (event, reason) => {
                                  if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
                                    repackSkipUomTabAdvanceRef.current = true;
                                  }
                                  setRepackOpenUomIdx(null);
                                },
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && repackOpenUomIdx !== index) {
                                  e.preventDefault();
                                  repackHandleTabRef.current?.(index, 'uom');
                                }
                              }}
                              displayEmpty disableUnderline disabled={isViewMode}
                              sx={{ fontWeight: 500, fontSize: '0.85rem', color: row.unitId ? 'inherit' : '#aaa' }}
                            >
                              <MenuItem value="">Select Unit</MenuItem>
                              {units.map((u) => (
                                <MenuItem key={u.id} value={u.id}>{u.abbreviation || u.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>

                        {/* Unit Qty */}
                        <TableCell align="right" sx={{ py: 1 }}>
                          <TextField
                            fullWidth variant="standard" type="number" placeholder="0.00"
                            value={row.unit}
                            onChange={(e) => repackUpdateRow(index, 'unit', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), repackHandleTabRef.current?.(index, 'unit'))}
                            onWheel={(e) => e.target.blur()}
                            inputRef={(el) => (repackUnitRefs.current[index] = el)}
                            disabled={isViewMode}
                            InputProps={{ disableUnderline: true, sx: { fontWeight: 700, fontSize: '0.85rem' }, inputProps: { style: { textAlign: 'right' }, min: 0 } }}
                          />
                        </TableCell>

                        {/* MRP */}
                        <TableCell align="right" sx={{ py: 1 }}>
                          <TextField
                            fullWidth variant="standard" type="number" placeholder="0"
                            value={row.mrp}
                            onChange={(e) => repackUpdateRow(index, 'mrp', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), repackHandleTabRef.current?.(index, 'mrp'))}
                            onWheel={(e) => e.target.blur()}
                            inputRef={(el) => (repackMrpRefs.current[index] = el)}
                            disabled={isViewMode}
                            InputProps={{ disableUnderline: true, sx: { fontWeight: 700, fontSize: '0.85rem', color: '#2e7d32' }, inputProps: { style: { textAlign: 'right' }, min: 0 } }}
                          />
                        </TableCell>

                        {/* Retail Rate */}
                        <TableCell align="right" sx={{ py: 1 }}>
                          <TextField
                            fullWidth variant="standard" type="number" placeholder="0"
                            value={row.retail}
                            onChange={(e) => repackUpdateRow(index, 'retail', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), repackHandleTabRef.current?.(index, 'retail'))}
                            onWheel={(e) => e.target.blur()}
                            inputRef={(el) => (repackRetailRefs.current[index] = el)}
                            disabled={isViewMode}
                            InputProps={{ disableUnderline: true, sx: { fontWeight: 700, fontSize: '0.85rem', color: '#c62828' }, inputProps: { style: { textAlign: 'right' }, min: 0 } }}
                          />
                        </TableCell>

                        {/* Wholesale Rate */}
                        <TableCell align="right" sx={{ py: 1 }}>
                          <TextField
                            fullWidth variant="standard" type="number" placeholder="0"
                            value={row.wSales}
                            onChange={(e) => repackUpdateRow(index, 'wSales', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), repackHandleTabRef.current?.(index, 'wSales'))}
                            onWheel={(e) => e.target.blur()}
                            inputRef={(el) => (repackWSalesRefs.current[index] = el)}
                            disabled={isViewMode}
                            InputProps={{ disableUnderline: true, sx: { fontWeight: 700, fontSize: '0.85rem', color: '#1565c0' }, inputProps: { style: { textAlign: 'right' }, min: 0 } }}
                          />
                        </TableCell>

                        {/* Delete row */}
                        <TableCell align="center" sx={{ py: 1 }}>
                          {!isViewMode && (
                            <IconButton size="small" color="error" onClick={() => repackDeleteRow(index)}
                              sx={{ opacity: 0.3, '&:hover': { opacity: 1 } }}>
                              <DeleteIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {/* Footer */}
              <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  Conversion: 1 Unit = <b>Qty</b> of {selectedUnit?.abbreviation || selectedUnit?.name || 'main unit'} • Barcodes are auto-generated.
                </Typography>
                {!isViewMode && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveRepacking}
                    disabled={repackSaving}
                    startIcon={repackSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      bgcolor: '#311b92', '&:hover': { bgcolor: '#1a237e' },
                      px: 3, py: 0.75, borderRadius: '4px', fontWeight: 700, fontSize: '0.75rem', boxShadow: 'none'
                    }}
                  >
                    Save Variants
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      )}
      {/* ─────────────────────────────────────────────────────────────────────── */}

      {/* Action Buttons — hidden on the Repacking tab (it has its own save) */}
      {!isViewMode && activeFormTab !== repackTabIndex && (
        <Paper
          elevation={0}
          sx={{
            p: layout.footerP,
            mt: layout.footerMt,
            bgcolor: 'grey.50',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            position: compact ? 'relative' : 'sticky',
            bottom: 0,
            zIndex: 5,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Stack direction="row" spacing={1}>
            <Button
              ref={submitButtonRef}
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              size={layout.submitSize}
            >
              {loading ? 'Saving...' : (initialData ? 'Update Product' : 'Create Product')}
            </Button>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
              startIcon={<CloseIcon />}
            >
              Cancel
            </Button>
          </Stack>
        </Paper>
      )}




      {/* Quick Create Dialogs */}
      {/* Category Dialog */}
      <Dialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: () => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                quickCategoryNameRef.current?.focus({ preventScroll: true });
              });
            });
          },
        }}
      >
        <DialogTitle>Create New Category</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              inputRef={quickCategoryNameRef}
              fullWidth
              label="Category Name"
              value={newCategory.name}
              onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
              autoComplete="off"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateCategory} variant="contained" startIcon={<AddIcon />}>
            Create
          </Button>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Unit Dialog */}
      <Dialog
        open={unitDialogOpen}
        onClose={() => setUnitDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: () => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                quickUnitNameRef.current?.focus({ preventScroll: true });
              });
            });
          },
        }}
      >
        <DialogTitle>Create New Unit</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              inputRef={quickUnitNameRef}
              fullWidth
              label="Unit Name"
              value={newUnit.name}
              onChange={(e) => setNewUnit(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Kilogram, Piece, Box"
              autoComplete="off"
            />
            <TextField
              fullWidth
              label="Abbreviation"
              value={newUnit.abbreviation}
              onChange={(e) => setNewUnit(prev => ({ ...prev, abbreviation: e.target.value }))}
              placeholder="e.g., kg, pcs, box"
              onKeyPress={(e) => e.key === 'Enter' && handleCreateUnit()}
              autoComplete="off"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateUnit} variant="contained" startIcon={<AddIcon />}>
            Create
          </Button>
          <Button onClick={() => setUnitDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Tax Dialog */}
      <Dialog
        open={taxDialogOpen}
        onClose={() => setTaxDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: () => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                quickTaxNameRef.current?.focus({ preventScroll: true });
              });
            });
          },
        }}
      >
        <DialogTitle>Create New Tax</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              inputRef={quickTaxNameRef}
              fullWidth
              label="Tax Name"
              value={newTax.name}
              onChange={(e) => setNewTax(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., GST, VAT"
              autoComplete="off"
            />
            <TextField
              fullWidth
              label="Tax Rate (%)"
              type="number"
              value={newTax.rate}
              onChange={(e) => setNewTax(prev => ({ ...prev, rate: e.target.value }))}
              placeholder="e.g., 18"
              onKeyPress={(e) => e.key === 'Enter' && handleCreateTax()}
              autoComplete="off"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateTax} variant="contained" startIcon={<AddIcon />}>
            Create
          </Button>
          <Button onClick={() => setTaxDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* HSN lookup — hsn.codes in an overlay; form stays underneath */}
      <Dialog
        open={hsnLookupDialogOpen}
        onClose={() => setHsnLookupDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span>HSN code lookup</span>
          <IconButton aria-label="Close" onClick={() => setHsnLookupDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 0,
            height: '70vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
          }}
        >
          <Typography variant="body2" sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
            Search by product name or code below, then close this window and paste the HSN into the field. If the page
            does not load here, use &quot;Open in new tab&quot;.
          </Typography>
          <Box
            component="iframe"
            src={HSN_LOOKUP_URL}
            title="HSN code lookup — hsn.codes"
            sx={{
              width: '100%',
              flex: 1,
              minHeight: { xs: 360, sm: 480 },
              border: 0,
              bgcolor: 'background.paper',
            }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Button
            href={HSN_LOOKUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
          >
            Open in new tab
          </Button>
          <Button variant="contained" onClick={() => setHsnLookupDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Keyboard Shortcuts & Tips</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="h6">Navigation</Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li><strong>Tab:</strong> Move to next field</li>
              <li><strong>Shift+Tab:</strong> Move to previous field</li>
              <li><strong>Ctrl+S:</strong> Save product</li>
              <li><strong>Esc:</strong> Cancel and close</li>
              <li><strong>Enter:</strong> Submit when in dialogs</li>
            </Box>

            <Typography variant="h6">Quick Actions</Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>Type in Category/Unit/Tax fields and select "+ Add New" to create inline</li>
              <li>SKU will be auto-generated if left empty</li>
              <li>Use arrow keys to navigate autocomplete suggestions</li>
            </Box>

            <Typography variant="h6">Tips</Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>All fields with * are required</li>
              <li>Min sale price cannot exceed selling price</li>
              <li>Image uploads are processed instantly</li>
              <li>Changes are not saved until you click Save or press Ctrl+S</li>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)} variant="contained">
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductFormEnhanced;
